// ─── Modèles de garde RÉCURRENTE (hors vacances) — porté MoPCal ───
// Catalogue de rythmes de garde + application sur le calendrier (garde par jour).
// Écrit calendriers/{calId}/jours/{date}.garde et mémorise le modèle courant
// sur calendriers/{calId}.gardeModele. Données PARTAGÉES MoPCal.
import { db, numeroSemaineISO } from '@mop/core';
import { doc, getDoc, setDoc, writeBatch } from 'firebase/firestore';

const P1 = 'parent1', P2 = 'parent2';
const pad = (n) => String(n).padStart(2, '0');
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const dateSeule = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
// Nombre de jours pleins entre l'ancre (date de départ CHOISIE) et d.
// AUCUN calage au lundi : le cycle démarre le JOUR choisi (pas forcément dim.→dim.).
const offsetJours = (ancreStr, d) => Math.round((dateSeule(d).getTime() - dateSeule(parse(ancreStr)).getTime()) / 86400000);

// N° de semaine ISO 8601 : centralisé dans @mop/core (numeroSemaineISO).

// label/desc = repli fr embarqué ; i18n/i18nDesc = clés traduites au point
// d'affichage (labelModeleGarde/descModeleGarde acceptent un `t` optionnel).
export const MODELES_GARDE = [
  { id: 'alternee', label: 'Semaine alternée', desc: 'Une semaine chez un parent, une chez l\'autre.',
    i18n: 'garde.mdlAlternee', i18nDesc: 'garde.mdlAlterneeDesc',
    build: (ancre) => ({ mode: 'alternance', parentSemaineA: P1, ancre }) },
  { id: '2-2-3', label: 'Garde 2-2-3', desc: '2 j / 2 j / 3 j, alternés sur 2 semaines.',
    i18n: 'garde.mdl223', i18nDesc: 'garde.mdl223Desc',
    build: (ancre) => ({ mode: 'joursSpecifiques', ancre, nbSemaines: 2,
      patternN: [P1, P1, P2, P2, P1, P1, P1, P2, P2, P1, P1, P2, P2, P2] }) },
  { id: '2-2-5-5', label: 'Garde 2-2-5-5', desc: '2 j chez chacun, puis 5 j chez chacun (cycle 14 j).',
    i18n: 'garde.mdl2255', i18nDesc: 'garde.mdl2255Desc',
    build: (ancre) => ({ mode: 'joursSpecifiques', ancre, nbSemaines: 2,
      patternN: [P1, P1, P2, P2, P1, P1, P1, P1, P1, P2, P2, P2, P2, P2] }) },
  { id: '3-4-4-3', label: 'Garde 3-4-4-3', desc: '3 j / 4 j alternés sur 2 semaines.',
    i18n: 'garde.mdl3443', i18nDesc: 'garde.mdl3443Desc',
    build: (ancre) => ({ mode: 'joursSpecifiques', ancre, nbSemaines: 2,
      patternN: [P1, P1, P1, P2, P2, P2, P2, P2, P2, P2, P1, P1, P1, P1] }) },
];
export const getModele = (id) => MODELES_GARDE.find(m => m.id === id) || null;

// Garde d'un jour selon une config de rythme. Le cycle est ANCRÉ sur la date de
// départ (config.ancre) : jour 0 = date choisie. Rétro-compat avec les anciennes
// configs (dateSemaineA / dateSemaineRef, calées au lundi).
export function gardePourDate(config, dateStr) {
  if (!config) return null;
  if (config.mode === 'semaineCalee') return gardePourDateCalee(config, dateStr);
  const ancre = config.ancre || config.dateSemaineA || config.dateSemaineRef;
  if (!ancre) return null;
  const off = offsetJours(ancre, parse(dateStr));
  if (off < 0) return null;
  if (config.mode === 'alternance') {
    const autre = config.parentSemaineA === P1 ? P2 : P1;
    return (Math.floor(off / 7) % 2 === 0) ? config.parentSemaineA : autre;
  }
  if (config.mode === 'joursSpecifiques') {
    const len = (config.nbSemaines || 2) * 7;
    return config.patternN[((off % len) + len) % len] || null;
  }
  return null;
}

// Garde d'un jour pour un motif CALÉ sur les semaines du calendrier (ISO, lundi).
// La ligne S1..Sn est choisie d'après le N° de semaine ISO (pas d'après la date
// de départ) : pour un cycle de 2 semaines, S1 = semaines paires ou impaires au
// choix ; sinon S(k) = (n° de semaine) mod nbSemaines. Colonne = lundi(0)…dim(6).
// L'applicabilité (à partir de quelle date) est gérée en amont par la période.
export function gardePourDateCalee(config, dateStr) {
  if (!config || !Array.isArray(config.patternN)) return null;
  const d = parse(dateStr);
  const w = numeroSemaineISO(d);
  const jour = (d.getDay() + 6) % 7;                    // lundi = 0 … dimanche = 6
  const nb = config.nbSemaines || 2;
  let ligne;
  if (nb === 2) {
    const semainePaire = (w % 2 === 0);
    const s1EstPaire = (config.semaine1 !== 'impaire'); // défaut : S1 = semaines paires
    ligne = (semainePaire === s1EstPaire) ? 0 : 1;
  } else {
    ligne = ((w % nb) + nb) % nb;
  }
  return config.patternN[ligne * 7 + jour] || null;
}

// Applique un modèle depuis `dateDebut` sur `jours` jours (défaut ~6 mois).
export async function appliquerModeleGarde(calId, modeleId, dateDebutStr, jours = 183) {
  const modele = getModele(modeleId);
  if (!modele) throw new Error('Modèle inconnu');
  const debut = dateDebutStr ? parse(dateDebutStr) : new Date();
  const config = modele.build(fmt(debut));   // ancre = jour de départ choisi

  const TAILLE = 400; let batch = writeBatch(db); let n = 0;
  const flush = async () => { if (n >= TAILLE) { await batch.commit(); batch = writeBatch(db); n = 0; } };
  let cur = new Date(debut);
  for (let i = 0; i < jours; i++) {
    const dk = fmt(cur);
    const g = gardePourDate(config, dk);
    if (g) { batch.set(doc(db, 'calendriers', calId, 'jours', dk), { garde: g }, { merge: true }); n++; await flush(); }
    cur.setDate(cur.getDate() + 1);
  }
  if (n > 0) await batch.commit();
  await setDoc(doc(db, 'calendriers', calId), { gardeModele: { id: modeleId, dateDebut: fmt(debut), config } }, { merge: true });
}

export async function lireGardeModele(calId) {
  try { const s = await getDoc(doc(db, 'calendriers', calId)); return s.exists() ? (s.data().gardeModele || null) : null; }
  catch { return null; }
}
